import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Radio, Send, Gavel, Zap, Plus, Play, SkipForward, Square, ShoppingBag, ArrowLeft, Timer, Trash2, Eye, Heart, Share2 } from "lucide-react";
import LiveVideo from "@/components/LiveVideo";

function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|live\/|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1`;
  const tw = url.match(/twitch\.tv\/([\w_]+)/);
  if (tw) return `https://player.twitch.tv/?channel=${tw[1]}&parent=${window.location.hostname}&autoplay=true`;
  return null;
}

export default function LiveSessionRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [state, setState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [remaining, setRemaining] = useState(null);
  const lastTsRef = useRef(null);
  const lastSeqRef = useRef(null);
  const chatEndRef = useRef(null);
  const prevCurrentRef = useRef(null);
  const metricsRef = useRef({ ewma: null, hist: [] });
  const cidRef = useRef((() => {
    let cid = window.sessionStorage.getItem("fripri_live_cid");
    if (!cid) { cid = Math.random().toString(36).slice(2, 12); window.sessionStorage.setItem("fripri_live_cid", cid); }
    return cid;
  })());
  const reactionRef = useRef(null);
  const pollRef = useRef(null);
  const modeRef = useRef("polling");
  const [viewerCount, setViewerCount] = useState(0);
  const [heartBursts, setHeartBursts] = useState(0);

  const loadSession = useCallback(() => {
    api.get(`/live/sessions/${id}`).then(r => setSession(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // Synchronisation temps réel : polling adaptatif + métriques client (RTT EWMA α=0.3, pertes sur 10 s)
  useEffect(() => {
    let stop = false;
    let timer = null;
    const m = metricsRef.current;
    const poll = async () => {
      const t0 = performance.now();
      const nowMs = Date.now();
      m.hist = m.hist.filter(h => nowMs - h.ts <= 10000);
      const fails = m.hist.filter(h => !h.ok).length;
      const lossPct = m.hist.length ? (fails / m.hist.length) * 100 : 0;
      try {
        const params = lastSeqRef.current !== null
          ? { since_seq: lastSeqRef.current }
          : (lastTsRef.current ? { since: lastTsRef.current } : {});
        const r = await api.get(`/live/sessions/${id}/state`, {
          params,
          headers: {
            "X-Fripri-CID": cidRef.current,
            "X-Fripri-RTT": String(Math.round(m.ewma || 0)),
            "X-Fripri-Loss": lossPct.toFixed(1),
          },
        });
        const rtt = performance.now() - t0;
        m.ewma = m.ewma === null ? rtt : 0.3 * rtt + 0.7 * m.ewma;
        m.hist.push({ ts: nowMs, ok: true });
        if (stop) return;
        setState(r.data);
        modeRef.current = r.data.transport_mode || "polling";
        setViewerCount(r.data.viewer_count || 0);
        const rc = r.data.reaction_count || 0;
        if (reactionRef.current !== null && rc > reactionRef.current) {
          setHeartBursts(b => b + Math.min(rc - reactionRef.current, 6));
        }
        reactionRef.current = rc;
        if (typeof r.data.last_seq === "number") lastSeqRef.current = r.data.last_seq;
        if (r.data.messages?.length) {
          setMessages(prev => {
            const known = new Set(prev.map(msg => msg.message_id));
            const fresh = r.data.messages.filter(msg => !known.has(msg.message_id));
            const merged = [...prev, ...fresh];
            return merged.slice(-150);
          });
          lastTsRef.current = r.data.messages[r.data.messages.length - 1].created_at;
        }
        const curKey = r.data.current ? `${r.data.current.live_product_id}:${r.data.current.status}:${r.data.current.bid_count}` : "none";
        if (prevCurrentRef.current !== null && prevCurrentRef.current !== curKey) loadSession();
        prevCurrentRef.current = curKey;
      } catch {
        m.hist.push({ ts: nowMs, ok: false });
      }
      if (!stop) {
        // Intervalle adaptatif : le polling devient filet de sécurité en mode temps réel
        timer = setTimeout(poll, modeRef.current === "realtime" ? 10000 : 2500);
      }
    };
    pollRef.current = poll;
    poll();
    // Poke via le canal de données LiveKit (T2) : resynchronisation immédiate par rejeu since_seq
    const onPoke = () => { if (!stop) { clearTimeout(timer); poll(); } };
    window.addEventListener("fripri-live-event", onPoke);
    return () => { stop = true; clearTimeout(timer); window.removeEventListener("fripri-live-event", onPoke); };
  }, [id, loadSession]);

  // Compte à rebours local
  useEffect(() => {
    const iv = setInterval(() => {
      const cur = state?.current;
      if (cur?.mode === "auction" && cur.ends_at && cur.status === "presenting") {
        setRemaining(Math.max(0, Math.round((new Date(cur.ends_at) - Date.now()) / 1000)));
      } else setRemaining(null);
    }, 300);
    return () => clearInterval(iv);
  }, [state]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Verrouille le défilement pendant le direct plein écran mobile
  const liveNow = (state?.status || session?.status) === "live";
  useEffect(() => {
    if (liveNow) {
      document.body.classList.add("live-lock");
      return () => document.body.classList.remove("live-lock");
    }
  }, [liveNow]);

  if (!session) return <div className="p-20 text-center">Chargement…</div>;

  const isHost = session.is_host;
  const status = state?.status || session.status;
  const current = state?.current;
  const embed = embedUrl(state?.video_url || session.video_url);

  return (
    <div className={`max-w-[1400px] mx-auto px-4 lg:px-8 py-6 has-bottom-nav ${status === "live" ? "" : "fade-in"}`} data-testid="live-room-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Link to="/brocante-live" className="btn btn-ghost btn-sm"><ArrowLeft size={14}/></Link>
          <div>
            <div className="flex items-center gap-2">
              {status === "live" && <span className="pill pill-coral inline-flex items-center gap-1.5" data-testid="live-badge"><Radio size={10}/> EN DIRECT</span>}
              {status === "live" && <span className="pill pill-outline inline-flex items-center gap-1.5" data-testid="viewer-count"><Eye size={10}/> {viewerCount}</span>}
              {status === "live" && <ShareLiveButton session={session} compact={false}/>}
              {status === "scheduled" && <span className="pill pill-mint">À venir</span>}
              {status === "ended" && <span className="pill">Terminée</span>}
            </div>
            <h1 className="text-2xl lg:text-3xl font-black tracking-tighter">{session.title}</h1>
            <p className="text-xs text-[color:var(--color-text-muted)]">Animé par {session.host_name}</p>
          </div>
        </div>
        {isHost && <HostSessionControls session={session} status={status} onChanged={loadSession}/>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Colonne gauche : vidéo + article en cours */}
        <div className="lg:col-span-2 space-y-4">
          <div className={`bg-black border-2 border-black flex items-center justify-center relative overflow-hidden ${status === "live" ? "max-lg:fixed max-lg:inset-0 max-lg:z-[90] max-lg:border-0 lg:aspect-video" : "aspect-video"}`} data-testid="video-container">
            {embed ? (
              <iframe src={embed} title="Live" className="w-full h-full" allow="autoplay; fullscreen" allowFullScreen/>
            ) : status === "live" ? (
              <LiveVideo sessionId={id} isHost={isHost} active={status === "live"}/>
            ) : (
              <div className="text-center text-white/60 p-8">
                <Radio size={40} className="mx-auto mb-3"/>
                <p className="font-bold">{status === "scheduled" ? "Le direct n'a pas encore commencé" : "Session terminée"}</p>
              </div>
            )}
            {status === "live" && (
              <>
                <FloatingHearts bursts={heartBursts}/>
                <HeartButton sessionId={id} user={user} onLocal={() => setHeartBursts(b => b + 1)}/>
                <MobileLiveOverlay session={session} current={current} remaining={remaining} user={user}
                  sessionId={id} isHost={isHost} viewerCount={viewerCount} messages={messages}/>
              </>
            )}
          </div>

          {current && <CurrentProduct current={current} remaining={remaining} user={user} sessionId={id} isHost={isHost} onAction={loadSession}/>}
          {!current && status === "live" && (
            <div className="p-6 border-2 border-dashed border-black text-center text-sm text-[color:var(--color-text-muted)]" data-testid="no-current-product">
              En attente du prochain article…
            </div>
          )}
          {status === "ended" && <ReplayPanel session={session}/>}

          {isHost && <HostQueue session={session} sessionId={id} status={status} onChanged={loadSession}/>}
        </div>

        {/* Colonne droite : chat */}
        <ChatPanel sessionId={id} messages={messages} user={user} status={status} canModerate={isHost || user?.role === "admin"}/>
      </div>
    </div>
  );
}

function CurrentProduct({ current, remaining, user, sessionId, isHost, onAction }) {
  const [bid, setBid] = useState("");
  const [busy, setBusy] = useState(false);
  const isAuction = current.mode === "auction";
  const minBid = current.bid_count ? current.current_price + 1 : current.price;

  const placeBid = async (amount) => {
    setBusy(true);
    try {
      const bidUuid = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      await api.post(`/live/sessions/${sessionId}/products/${current.live_product_id}/bid`, { amount, bid_uuid: bidUuid });
      toast.success(`Offre de ${amount.toFixed(2)}€ placée ⚡`);
      setBid("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const buy = async () => {
    setBusy(true);
    try {
      await api.post(`/live/sessions/${sessionId}/products/${current.live_product_id}/buy`);
      toast.success("C'est à toi ! Article ajouté au panier 🎉");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`border-2 border-black p-4 ${isAuction ? "bg-[color:var(--color-yellow)]" : "bg-[color:var(--color-mint)]"}`} data-testid="current-product-card">
      <div className="flex gap-4">
        <div className="w-24 h-24 lg:w-32 lg:h-32 shrink-0 border-2 border-black bg-white overflow-hidden">
          {current.product_image && <img src={fileUrl(current.product_image)} alt="" className="w-full h-full object-cover"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="pill bg-black text-white inline-flex items-center gap-1">
              {isAuction ? <><Gavel size={10}/> Enchère flash</> : <><Zap size={10}/> Prix fixe</>}
            </span>
            {isAuction && remaining !== null && (
              <span className={`pill inline-flex items-center gap-1 ${remaining <= 10 ? "pill-coral" : "pill-outline bg-white"}`} data-testid="live-countdown">
                <Timer size={10}/> {remaining}s
              </span>
            )}
          </div>
          <p className="label mt-1">{current.product_brand || "—"}</p>
          <h3 className="font-black text-lg lg:text-xl tracking-tight truncate">{current.product_title}</h3>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="display-num text-3xl font-black" style={{ color: "var(--color-coral)" }} data-testid="current-price">{current.current_price?.toFixed(2)}€</p>
            {isAuction && current.top_bidder_name && <p className="text-xs font-bold">👑 {current.top_bidder_name} · {current.bid_count} offre(s)</p>}
          </div>
        </div>
      </div>

      {user && !isHost && user.user_id !== current.seller_id && (
        <div className="mt-3 flex gap-2">
          {isAuction ? (
            <>
              <input
                type="number" step="0.5" min={minBid}
                value={bid} onChange={e => setBid(e.target.value)}
                placeholder={`Min. ${minBid.toFixed(2)}€`}
                className="field flex-1 bg-white" data-testid="bid-input"
              />
              <button onClick={() => placeBid(parseFloat(bid))} disabled={busy || !bid || parseFloat(bid) < minBid} className="btn btn-primary btn-sm" data-testid="place-bid-btn">
                <Gavel size={14}/> Enchérir
              </button>
              <button onClick={() => placeBid(minBid)} disabled={busy} className="btn btn-coral btn-sm" data-testid="quick-bid-btn">
                +{minBid.toFixed(0)}€
              </button>
            </>
          ) : (
            <button onClick={buy} disabled={busy} className="btn btn-primary w-full" data-testid="live-buy-btn">
              <ShoppingBag size={16}/> Acheter maintenant — {current.price?.toFixed(2)}€
            </button>
          )}
        </div>
      )}
      {!user && <Link to="/login" className="btn btn-primary btn-sm mt-3 inline-flex">Connecte-toi pour participer</Link>}

      {isHost && (
        <div className="mt-3 flex gap-2">
          {isAuction && current.top_bidder_id && (
            <HostAction sessionId={sessionId} lpid={current.live_product_id} action="finalize" label="🔨 Adjuger maintenant" onDone={onAction}/>
          )}
          <HostAction sessionId={sessionId} lpid={current.live_product_id} action="pass" label="Retirer" ghost onDone={onAction}/>
        </div>
      )}
    </div>
  );
}

function HostAction({ sessionId, lpid, action, label, ghost, onDone }) {
  const run = async () => {
    try {
      await api.post(`/live/sessions/${sessionId}/products/${lpid}/close`, { action });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return <button onClick={run} className={`btn btn-sm ${ghost ? "btn-ghost" : "btn-primary"}`} data-testid={`host-${action}-btn`}>{label}</button>;
}

function HostSessionControls({ session, status, onChanged }) {
  const act = async (action) => {
    try {
      await api.patch(`/live/sessions/${session.session_id}`, { action });
      toast.success(action === "start" ? "Session démarrée 🔴" : "Session terminée");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="flex gap-2" data-testid="host-controls">
      {status !== "live" && status !== "ended" && (
        <button onClick={() => act("start")} className="btn btn-coral btn-sm" data-testid="start-session-btn"><Play size={14}/> Démarrer le direct</button>
      )}
      {status === "live" && (
        <button onClick={() => act("end")} className="btn btn-primary btn-sm" data-testid="end-session-btn"><Square size={14}/> Terminer</button>
      )}
    </div>
  );
}

function HostQueue({ session, sessionId, status, onChanged }) {
  const { user } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  const queue = (session.products || []).filter(p => ["queued", "passed"].includes(p.status));
  const sold = (session.products || []).filter(p => p.status === "sold");

  const present = async (lp) => {
    try {
      await api.post(`/live/sessions/${sessionId}/products/${lp.live_product_id}/present`);
      toast.success("Article présenté 🛍");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="border-2 border-black bg-white p-4" data-testid="host-queue">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-black tracking-tight">File d'articles ({queue.length})</h3>
        <button onClick={() => setAddOpen(true)} className="btn btn-mint btn-sm" data-testid="add-live-product-btn"><Plus size={14}/> Ajouter</button>
      </div>
      <div className="space-y-2">
        {queue.length === 0 && <p className="text-xs text-[color:var(--color-text-muted)]">Aucun article en attente. Ajoute tes articles à présenter.</p>}
        {queue.map(lp => (
          <div key={lp.live_product_id} className="flex items-center gap-3 p-2 border border-black/20" data-testid={`queue-item-${lp.live_product_id}`}>
            <div className="w-12 h-12 shrink-0 bg-[color:var(--color-surface)] overflow-hidden">
              {lp.product_image && <img src={fileUrl(lp.product_image)} alt="" className="w-full h-full object-cover"/>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">{lp.product_title}</p>
              <p className="text-xs text-[color:var(--color-text-muted)]">{lp.mode === "auction" ? `Enchère · départ ${lp.price.toFixed(0)}€ · ${lp.duration_seconds}s` : `Prix fixe · ${lp.price.toFixed(0)}€`}{lp.status === "passed" ? " · retiré" : ""}</p>
            </div>
            <button onClick={() => present(lp)} disabled={status !== "live"} className="btn btn-primary btn-sm shrink-0 disabled:opacity-40" data-testid={`present-${lp.live_product_id}`}>
              <SkipForward size={12}/> Présenter
            </button>
          </div>
        ))}
        {sold.length > 0 && (
          <p className="text-xs font-bold pt-2 border-t border-black/10">✓ {sold.length} article(s) vendu(s) : {sold.reduce((a, p) => a + (p.sold_price || 0), 0).toFixed(2)}€</p>
        )}
      </div>
      {addOpen && <AddProductModal user={user} sessionId={sessionId} hostId={session.host_id} onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); onChanged(); }}/>}
    </div>
  );
}

function AddProductModal({ user, sessionId, hostId, onClose, onAdded }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_id: "", mode: "auction", price: "", duration_seconds: 60 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/products?seller_id=${hostId}&status=active`).then(r => setProducts(r.data)).catch(() => {});
  }, [hostId]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/live/sessions/${sessionId}/products`, {
        product_id: form.product_id,
        mode: form.mode,
        price: parseFloat(form.price),
        duration_seconds: parseInt(form.duration_seconds),
      });
      toast.success("Article ajouté à la file ✓");
      onAdded();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="add-product-modal">
      <form onClick={e => e.stopPropagation()} onSubmit={submit} className="bg-white border-2 border-black w-full max-w-md p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-2xl font-black tracking-tighter">Ajouter un article</h3>
          <button type="button" onClick={onClose} className="text-2xl leading-none">×</button>
        </div>
        <div>
          <label className="label font-bold mb-1 block">Article</label>
          <select className="field" required value={form.product_id} onChange={e => {
            const p = products.find(x => x.product_id === e.target.value);
            setForm({ ...form, product_id: e.target.value, price: p ? String(p.price) : form.price });
          }} data-testid="lp-product-select">
            <option value="">— Choisir un article actif —</option>
            {products.map(p => <option key={p.product_id} value={p.product_id}>{p.title} ({p.price?.toFixed(0)}€)</option>)}
          </select>
        </div>
        <div>
          <label className="label font-bold mb-1 block">Mode de vente</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setForm({ ...form, mode: "auction" })} className={`btn btn-sm flex-1 ${form.mode === "auction" ? "btn-primary" : "btn-ghost"}`} data-testid="mode-auction-btn"><Gavel size={12}/> Enchère flash</button>
            <button type="button" onClick={() => setForm({ ...form, mode: "fixed" })} className={`btn btn-sm flex-1 ${form.mode === "fixed" ? "btn-primary" : "btn-ghost"}`} data-testid="mode-fixed-btn"><Zap size={12}/> Prix fixe</button>
          </div>
        </div>
        <div>
          <label className="label font-bold mb-1 block">{form.mode === "auction" ? "Prix de départ (€)" : "Prix de vente (€)"}</label>
          <input className="field" type="number" step="0.5" min="0.5" required value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} data-testid="lp-price-input"/>
        </div>
        {form.mode === "auction" && (
          <div>
            <label className="label font-bold mb-1 block">Durée de l'enchère (secondes)</label>
            <select className="field" value={form.duration_seconds} onChange={e => setForm({ ...form, duration_seconds: e.target.value })} data-testid="lp-duration-select">
              {[30, 60, 90, 120, 180, 300].map(s => <option key={s} value={s}>{s}s</option>)}
            </select>
          </div>
        )}
        <button type="submit" disabled={saving || !form.product_id} className="btn btn-primary w-full" data-testid="lp-submit-btn">
          {saving ? "Ajout…" : "Ajouter à la file →"}
        </button>
      </form>
    </div>
  );
}

function ChatPanel({ sessionId, messages, user, status, canModerate }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [deletedIds, setDeletedIds] = useState([]);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const deleteMsg = async (m) => {
    try {
      await api.delete(`/live/sessions/${sessionId}/messages/${m.message_id}`);
      setDeletedIds(ids => [...ids, m.message_id]);
      toast.success("Message supprimé");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const visible = messages.filter(m => !deletedIds.includes(m.message_id));

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/live/sessions/${sessionId}/chat`, { content: text.trim() });
      setText("");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSending(false); }
  };

  return (
    <div className="border-2 border-black bg-white flex flex-col h-[500px] lg:h-auto lg:max-h-[calc(100vh-200px)]" data-testid="chat-panel">
      <div className="p-3 border-b-2 border-black font-black tracking-tight shrink-0">💬 Chat live</div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
        {visible.length === 0 && <p className="text-xs text-[color:var(--color-text-muted)] text-center py-8">Le chat est vide. Dis bonjour !</p>}
        {visible.map(m => (
          <div key={m.message_id} className={`text-sm group flex items-start gap-1 ${m.type === "system" || m.type === "sold" || m.type === "present" ? "bg-[color:var(--color-surface)] p-2 text-xs font-bold" : ""}`} data-testid={`chat-msg-${m.message_id}`}>
            <div className="flex-1 min-w-0">
              {m.type === "bid" ? (
                <p className="text-xs"><span className="font-black text-[color:var(--color-coral)]">⚡ {m.user_name}</span> {m.content}</p>
              ) : m.type === "chat" ? (
                <p><span className="font-black">{m.user_name} :</span> {m.content}</p>
              ) : (
                <p>{m.content}</p>
              )}
            </div>
            {canModerate && m.type === "chat" && (
              <button onClick={()=>deleteMsg(m)} className="shrink-0 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-coral)] p-1" title="Supprimer ce message" aria-label="Supprimer ce message" data-testid={`del-chat-msg-${m.message_id}`}>
                <Trash2 size={12}/>
              </button>
            )}
          </div>
        ))}
        <div ref={endRef}/>
      </div>
      <form onSubmit={send} className="p-3 border-t-2 border-black flex gap-2 shrink-0">
        {user ? (
          <>
            <input
              value={text} onChange={e => setText(e.target.value)}
              placeholder={status === "live" ? "Ton message…" : "Chat ouvert pendant le direct"}
              disabled={status !== "live"}
              className="field flex-1" maxLength={500} data-testid="chat-input"
            />
            <button type="submit" disabled={sending || status !== "live" || !text.trim()} className="btn btn-primary btn-sm disabled:opacity-40" data-testid="chat-send-btn"><Send size={14}/></button>
          </>
        ) : (
          <Link to="/login" className="btn btn-ghost btn-sm w-full">Connecte-toi pour chatter</Link>
        )}
      </form>
    </div>
  );
}

function FloatingHearts({ bursts }) {
  const [hearts, setHearts] = useState([]);
  const prevRef = useRef(0);
  useEffect(() => {
    const delta = bursts - prevRef.current;
    prevRef.current = bursts;
    if (delta <= 0) return;
    const fresh = Array.from({ length: delta }, () => ({
      id: Math.random().toString(36).slice(2),
      left: 8 + Math.random() * 20,
      size: 18 + Math.random() * 14,
      hue: Math.random() > 0.5 ? "#ff5470" : "#ff8fa3",
    }));
    setHearts(h => [...h, ...fresh].slice(-30));
    const ids = fresh.map(f => f.id);
    setTimeout(() => setHearts(h => h.filter(x => !ids.includes(x.id))), 2600);
  }, [bursts]);
  return (
    <div className="absolute bottom-16 max-lg:bottom-44 right-0 w-24 h-72 pointer-events-none z-20 overflow-hidden" data-testid="floating-hearts">
      {hearts.map(h => (
        <span key={h.id} className="heart-float absolute bottom-0" style={{ right: `${h.left}px`, fontSize: h.size, color: h.hue }}>❤</span>
      ))}
    </div>
  );
}

function HeartButton({ sessionId, user, onLocal }) {
  const lastRef = useRef(0);
  const tap = async () => {
    if (!user) { toast.error("Connecte-toi pour réagir"); return; }
    const now = Date.now();
    if (now - lastRef.current < 300) return;
    lastRef.current = now;
    onLocal();
    try { await api.post(`/live/sessions/${sessionId}/react`); } catch {}
  };
  return (
    <button onClick={tap} className="absolute bottom-3 max-lg:bottom-28 right-3 z-30 w-12 h-12 border-2 border-white bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-[color:var(--color-coral)] active:scale-90 transition-transform" title="Envoyer un cœur" data-testid="react-heart-btn">
      <Heart size={20} fill="currentColor"/>
    </button>
  );
}

function MobileLiveOverlay({ session, current, remaining, user, sessionId, isHost, viewerCount, messages }) {
  const [busy, setBusy] = useState(false);
  const isAuction = current?.mode === "auction";
  const minBid = current ? (current.bid_count ? current.current_price + 1 : current.price) : 0;
  const lastMsgs = messages.filter(m => m.type === "chat" || m.type === "bid").slice(-3);

  const quickBid = async () => {
    setBusy(true);
    try {
      const bidUuid = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      await api.post(`/live/sessions/${sessionId}/products/${current.live_product_id}/bid`, { amount: minBid, bid_uuid: bidUuid });
      toast.success(`Offre de ${minBid.toFixed(2)}€ placée ⚡`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const quickBuy = async () => {
    setBusy(true);
    try {
      await api.post(`/live/sessions/${sessionId}/products/${current.live_product_id}/buy`);
      toast.success("C'est à toi ! Article ajouté au panier 🎉");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="lg:hidden absolute inset-0 pointer-events-none z-20 flex flex-col" data-testid="mobile-live-overlay">
      {/* Barre supérieure */}
      <div className="flex items-center gap-2 p-3 pt-[max(env(safe-area-inset-top),12px)] bg-gradient-to-b from-black/70 to-transparent pointer-events-auto">
        <Link to="/brocante-live" className="w-9 h-9 border-2 border-white text-white flex items-center justify-center bg-black/40" data-testid="mobile-live-back"><ArrowLeft size={16}/></Link>
        <span className="pill pill-coral inline-flex items-center gap-1"><Radio size={9}/> LIVE</span>
        <span className="pill bg-black/50 text-white inline-flex items-center gap-1 border border-white/40"><Eye size={9}/> {viewerCount}</span>
        <p className="text-white text-xs font-bold truncate flex-1">{session.title}</p>
        <ShareLiveButton session={session} compact/>
      </div>

      <div className="flex-1"/>

      {/* Derniers messages du chat par-dessus la vidéo */}
      <div className="px-3 space-y-1 pointer-events-none mb-2 max-w-[75%]">
        {lastMsgs.map(m => (
          <p key={m.message_id} className="text-white text-xs leading-snug [text-shadow:0_1px_3px_rgba(0,0,0,0.9)]">
            <span className="font-black">{m.user_name}</span>{m.type === "bid" ? " ⚡ " : " : "}{m.content}
          </p>
        ))}
      </div>

      {/* Carte article + enchère en 1 tap */}
      {current && (
        <div className="mx-3 mb-[max(env(safe-area-inset-bottom),12px)] pointer-events-auto bg-black/60 backdrop-blur-md border border-white/30 p-3" data-testid="mobile-product-overlay">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 shrink-0 border border-white/40 bg-white overflow-hidden">
              {current.product_image && <img src={fileUrl(current.product_image)} alt="" className="w-full h-full object-cover"/>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-black truncate">{current.product_title}</p>
              <div className="flex items-center gap-2">
                <p className="text-[color:var(--color-mint)] font-black text-xl" data-testid="mobile-current-price">{current.current_price?.toFixed(2)}€</p>
                {isAuction && remaining !== null && (
                  <span className={`text-xs font-black px-1.5 py-0.5 ${remaining <= 10 ? "bg-[color:var(--color-coral)] text-white" : "bg-white text-black"}`}>
                    <Timer size={9} className="inline mr-0.5"/>{remaining}s
                  </span>
                )}
              </div>
              {isAuction && current.top_bidder_name && <p className="text-white/70 text-[10px] font-bold truncate">👑 {current.top_bidder_name} · {current.bid_count} offre(s)</p>}
            </div>
            {user && !isHost && user.user_id !== current.seller_id && (
              isAuction ? (
                <button onClick={quickBid} disabled={busy} className="btn btn-coral shrink-0 px-4 py-3 font-black" data-testid="mobile-quick-bid-btn">
                  <Gavel size={16}/> {minBid.toFixed(0)}€
                </button>
              ) : (
                <button onClick={quickBuy} disabled={busy} className="btn btn-coral shrink-0 px-4 py-3 font-black" data-testid="mobile-quick-buy-btn">
                  <ShoppingBag size={16}/> Acheter
                </button>
              )
            )}
            {!user && <Link to="/login" className="btn btn-coral shrink-0 px-3 py-2 text-xs">Se connecter</Link>}
          </div>
        </div>
      )}
    </div>
  );
}

function ReplayPanel({ session }) {
  const products = session.products || [];
  const sold = products.filter(p => p.status === "sold");
  const availables = products.filter(p => p.status !== "sold" && p.catalog_status === "available");
  const total = sold.reduce((sum, p) => sum + (p.sold_price || 0), 0);
  if (!products.length && !session.replay_path) return null;
  return (
    <div className="border-2 border-black bg-white" data-testid="replay-panel">
      <div className="p-4 bg-black text-white flex items-center justify-between flex-wrap gap-2">
        <p className="font-black tracking-tight inline-flex items-center gap-2"><Play size={16}/> Résumé du live</p>
        <p className="text-xs font-bold">{products.length} article(s) présentés · {sold.length} vendu(s){total > 0 ? ` · ${total.toFixed(2)}€` : ""}</p>
      </div>
      {(session.peak_viewers > 0 || session.total_reactions > 0) && (
        <div className="p-3 border-b-2 border-black grid grid-cols-3 gap-2 text-center" data-testid="host-stats">
          <div><p className="font-black text-lg">{session.peak_viewers || 0}</p><p className="label text-[10px] leading-tight">Pic</p></div>
          <div><p className="font-black text-lg">{session.total_reactions || 0}</p><p className="label text-[10px] leading-tight">Réactions ❤</p></div>
          <div><p className="font-black text-lg">{sold.length}</p><p className="label text-[10px] leading-tight">Ventes</p></div>
        </div>
      )}
      {session.replay_path && (
        <div className="border-b-2 border-black bg-black">
          <video controls playsInline preload="metadata" className="w-full max-h-[420px]" data-testid="replay-video"
            src={`${process.env.REACT_APP_BACKEND_URL}/api/live/sessions/${session.session_id}/replay-video`}/>
        </div>
      )}
      {availables.length > 0 && (
        <div className="p-4 border-b-2 border-black bg-[color:var(--color-mint)]">
          <p className="text-xs font-black uppercase tracking-wider mb-2">💚 Encore disponibles — dernière chance !</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {availables.map(p => (
              <Link key={p.live_product_id} to={`/produit/${p.product_id}`} className="flex items-center gap-3 p-2 bg-white border-2 border-black card-hover" data-testid={`replay-available-${p.live_product_id}`}>
                <div className="w-12 h-12 shrink-0 border border-black bg-white overflow-hidden">
                  {p.product_image && <img src={fileUrl(p.product_image)} alt="" className="w-full h-full object-cover"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{p.product_title}</p>
                  <p className="text-xs font-black text-[color:var(--color-coral)]">{(p.current_price || p.price)?.toFixed(2)}€</p>
                </div>
                <span className="btn btn-coral btn-sm shrink-0"><ShoppingBag size={12}/> Acheter</span>
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="p-4 space-y-1.5">
        {products.map(p => (
          <div key={p.live_product_id} className="flex items-center gap-3 text-sm" data-testid={`replay-item-${p.live_product_id}`}>
            <span className={`pill text-[10px] shrink-0 ${p.status === "sold" ? "pill-coral" : "pill-outline"}`}>{p.status === "sold" ? "VENDU" : "Non vendu"}</span>
            <p className="truncate flex-1 font-bold">{p.product_title}</p>
            {p.status === "sold" && <p className="text-xs font-black shrink-0">{p.sold_price?.toFixed(2)}€ {p.winner_name ? `· 👑 ${p.winner_name}` : ""}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ShareLiveButton({ session, compact }) {
  const share = async (e) => {
    e.preventDefault();
    const url = `https://fripri.com/brocante-live/${session.session_id}`;
    const text = `🔴 Rejoins-moi sur la Brocante Live Fripri : « ${session.title} » — enchères et bonnes affaires en direct !`;
    if (navigator.share) {
      try { await navigator.share({ title: "Brocante Live Fripri", text, url }); return; } catch { return; }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Lien copié ! Colle-le où tu veux 📋");
    } catch {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank");
    }
  };
  if (compact) {
    return (
      <button onClick={share} className="w-9 h-9 border-2 border-white text-white flex items-center justify-center bg-black/40 shrink-0" title="Partager le live" aria-label="Partager le live" data-testid="share-live-btn-mobile">
        <Share2 size={15}/>
      </button>
    );
  }
  return (
    <button onClick={share} className="pill pill-outline inline-flex items-center gap-1.5 cursor-pointer hover:bg-black hover:text-white" title="Partager le live" data-testid="share-live-btn">
      <Share2 size={10}/> Partager
    </button>
  );
}
